import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { taskSpeedsApi } from "@api/taskSpeeds";

function digitsOnly(v) {
  return String(v ?? "").replace(/[^\d]/g, "");
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function TaskSpeedsPage() {
  const { uid } = useParams();
  const taskUid = Number(uid);
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // create/edit form
  const [editUid, setEditUid] = useState(null);
  const [level, setLevel] = useState("1");
  const [title, setTitle] = useState("");
  const [subsMin, setSubsMin] = useState("1");
  const [subsMax, setSubsMax] = useState("3");
  const [threads, setThreads] = useState("1");
  const [delayMinSec, setDelayMinSec] = useState("2");
  const [delayMaxSec, setDelayMaxSec] = useState("5");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await taskSpeedsApi.list(taskUid);
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Не удалось загрузить скорости задачи");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Number.isFinite(taskUid) || taskUid < 1) return;
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskUid]);

  const isEditing = editUid != null;

  const values = useMemo(() => {
    const lv = clamp(Number(digitsOnly(level) || 1), 1, 999);
    const sMin = clamp(Number(digitsOnly(subsMin) || 1), 0, 999);
    const sMax = clamp(Number(digitsOnly(subsMax) || sMin), sMin, 999);
    const th = clamp(Number(digitsOnly(threads) || 1), 1, 999);
    const dMinSec = clamp(Number(digitsOnly(delayMinSec) || 0), 0, 999999);
    const dMaxSec = clamp(Number(digitsOnly(delayMaxSec) || dMinSec), dMinSec, 999999);
    return {
      lv,
      sMin,
      sMax,
      th,
      dMin: dMinSec * 1000,
      dMax: dMaxSec * 1000,
    };
  }, [level, subsMin, subsMax, threads, delayMinSec, delayMaxSec]);

  const resetForm = () => {
    setEditUid(null);
    setLevel("1");
    setTitle("");
    setSubsMin("1");
    setSubsMax("3");
    setThreads("1");
    setDelayMinSec("2");
    setDelayMaxSec("5");
  };

  const onEdit = (s) => {
    setEditUid(s.uid);
    setLevel(String(s.level ?? 1));
    setTitle(String(s.title ?? ""));
    setSubsMin(String(s.subsMin ?? 1));
    setSubsMax(String(s.subsMax ?? 1));
    setThreads(String(s.threads ?? 1));
    setDelayMinSec(String(Math.round((Number(s.delayMinMs ?? 0) || 0) / 1000)));
    setDelayMaxSec(String(Math.round((Number(s.delayMaxMs ?? 0) || 0) / 1000)));
  };

  const onSave = async () => {
    try {
      const payload = {
        level: values.lv,
        title,
        subsMin: values.sMin,
        subsMax: values.sMax,
        threads: values.th,
        delayMinMs: values.dMin,
        delayMaxMs: values.dMax,
      };

      if (isEditing) {
        const r = await taskSpeedsApi.update(taskUid, editUid, payload);
        if (!r?.success) throw new Error(r?.message || "Ошибка обновления");
      } else {
        const r = await taskSpeedsApi.create(taskUid, payload);
        if (!r?.success) throw new Error(r?.message || "Ошибка создания");
      }

      resetForm();
      await load();
    } catch (e) {
      alert(e?.message || "Ошибка сохранения");
    }
  };

  const onDelete = async (speedUid) => {
    if (!window.confirm(`Удалить скорость uid=${speedUid} в задаче #${taskUid}?`)) return;
    try {
      const r = await taskSpeedsApi.remove(taskUid, speedUid);
      if (!r?.success) throw new Error(r?.message || "Ошибка удаления");
      if (editUid === speedUid) resetForm();
      await load();
    } catch (e) {
      alert(e?.message || "Ошибка удаления");
    }
  };

  const card = "rounded-[18px] border border-[#E5E7EB] bg-white";
  const label = "text-[12px] font-semibold text-[#374151]";
  const input =
    "h-11 w-full rounded-[14px] border border-[#D6D9E0] bg-white px-4 outline-none focus:border-[#2B7CFF]";
  const btn =
    "h-10 px-5 rounded-full bg-[#2B7CFF] text-white shadow-[0_12px_28px_rgba(43,124,255,0.35)] hover:brightness-95 transition text-sm font-semibold disabled:opacity-60";
  const ghost =
    "h-10 px-5 rounded-full border border-[#D6D9E0] bg-white hover:bg-[#F7F8FA] transition text-sm font-medium";

  return (
    <div className="mx-auto max-w-[1220px] px-6 pb-12 pt-24">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[20px] font-semibold text-[#111827]">Скорости задачи</div>
          <div className="text-[13px] text-[#6B7280]">Задача #{taskUid}</div>
        </div>

        <button type="button" className={ghost} onClick={() => navigate("/")}>Назад к задачам</button>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-5">
        <div className={`${card} p-4`}>
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-semibold text-[#111827]">{isEditing ? `Редактирование (uid ${editUid})` : "Новая скорость"}</div>
            {isEditing && (
              <button type="button" className="text-[12px] font-semibold text-[#2B7CFF] hover:underline" onClick={resetForm}>
                Сбросить
              </button>
            )}
          </div>

          <div className="mt-4 grid gap-3">
            <label className="grid gap-2">
              <span className={label}>Уровень</span>
              <input className={input} value={level} onChange={(e) => setLevel(digitsOnly(e.target.value))} />
            </label>

            <label className="grid gap-2">
              <span className={label}>Название (опционально)</span>
              <input className={input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="например: аккуратно" />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-2">
                <span className={label}>Подписок от</span>
                <input className={input} value={subsMin} onChange={(e) => setSubsMin(digitsOnly(e.target.value))} />
              </label>
              <label className="grid gap-2">
                <span className={label}>Подписок до</span>
                <input className={input} value={subsMax} onChange={(e) => setSubsMax(digitsOnly(e.target.value))} />
              </label>
            </div>

            <label className="grid gap-2">
              <span className={label}>Потоков</span>
              <input className={input} value={threads} onChange={(e) => setThreads(digitsOnly(e.target.value))} />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-2">
                <span className={label}>Задержка от (сек)</span>
                <input className={input} value={delayMinSec} onChange={(e) => setDelayMinSec(digitsOnly(e.target.value))} />
              </label>
              <label className="grid gap-2">
                <span className={label}>Задержка до (сек)</span>
                <input className={input} value={delayMaxSec} onChange={(e) => setDelayMaxSec(digitsOnly(e.target.value))} />
              </label>
            </div>

            <button type="button" className={btn} onClick={onSave} disabled={loading}>
              {isEditing ? "Сохранить" : "Добавить"}
            </button>
          </div>
        </div>

        <div className={`${card} p-4`}>
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-semibold text-[#111827]">Список скоростей</div>
            <button type="button" className={ghost} onClick={() => load()} disabled={loading}>
              Обновить
            </button>
          </div>

          {error && <div className="mt-3 text-[13px] text-red-600">{error}</div>}

          <div className="mt-4 overflow-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[#6B7280]">
                  <th className="py-2 pr-4">UID</th>
                  <th className="py-2 pr-4">Уровень</th>
                  <th className="py-2 pr-4">Подписки</th>
                  <th className="py-2 pr-4">Потоки</th>
                  <th className="py-2 pr-4">Задержка</th>
                  <th className="py-2 pr-4">Действия</th>
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.uid} className="border-t border-[#EEF0F3]">
                    <td className="py-3 pr-4 font-semibold text-[#111827]">{s.uid}</td>
                    <td className="py-3 pr-4">{s.level}{s.title ? ` — ${s.title}` : ""}</td>
                    <td className="py-3 pr-4">{s.subsMin}–{s.subsMax}</td>
                    <td className="py-3 pr-4">{s.threads}</td>
                    <td className="py-3 pr-4">
                      {Math.round((Number(s.delayMinMs || 0) / 1000))}–{Math.round((Number(s.delayMaxMs || 0) / 1000))} сек
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <button type="button" className="h-9 px-4 rounded-full border border-[#D6D9E0] bg-white hover:bg-[#F7F8FA] transition text-[13px] font-medium" onClick={() => onEdit(s)}>
                          Редактировать
                        </button>
                        <button type="button" className="h-9 px-4 rounded-full border border-[#FCA5A5] bg-white text-[#B91C1C] hover:bg-[#FFF1F2] transition text-[13px] font-medium" onClick={() => onDelete(s.uid)}>
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!list.length && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-[#6B7280]">
                      {loading ? "Загрузка…" : "Скоростей пока нет — добавь первую слева."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
